import * as path from "node:path";
import { runAz } from "./az.js";
import type { ClassifiedResult, ClassifiedChange } from "./whatif-noise.js";
import { redactForLog, redactForReport, redactWhatIfFailure } from "./whatif-redact.js";

// Structured ARM What-If result (from `az deployment group what-if --no-pretty-print -o json`).
export interface WhatIfPropertyChange {
  path: string;
  propertyChangeType: string; // Modify | Create | Delete | Array | NoEffect | NoChange
  before?: unknown;
  after?: unknown;
  children?: WhatIfPropertyChange[];
}
export interface WhatIfChange {
  resourceId: string;
  changeType: string; // Modify | Unsupported | NoChange | Ignore | Create | Delete
  delta?: WhatIfPropertyChange[];
  unsupportedReason?: string;
}
export interface WhatIfResult { changes?: WhatIfChange[] }

/**
 * Derive the ARM resource type ("Microsoft.X/type[/subtype]") from a resourceId.
 * Returns '' for an ARM-expression resourceId (Unsupported changes carry an
 * `[extensionResourceId(...)]` expression, not a normal id).
 */
export function deriveResourceType(resourceId: string): string {
  if (resourceId.startsWith("[")) return "";
  const afterProviders = resourceId.split("/providers/")[1];
  if (!afterProviders) return "";
  const segs = afterProviders.split("/");
  const parts = [segs[0]]; // namespace
  for (let i = 1; i < segs.length; i += 2) parts.push(segs[i]); // type segments at odd indices
  return parts.join("/");
}

/** Run `az deployment group what-if --no-pretty-print` and return the changes. Read-only. */
export async function runWhatIf(opts: { resourceGroup: string; repoRoot: string; params: string[] }): Promise<WhatIfChange[]> {
  const bicepPath = path.join(opts.repoRoot, "deploy", "main.bicep");
  const out = await runAz([
    "deployment", "group", "what-if",
    "--resource-group", opts.resourceGroup,
    "--template-file", bicepPath,
    "--parameters", ...opts.params,
    "--no-pretty-print",
    "--output", "json",
  ]);
  const result = JSON.parse(out) as WhatIfResult;
  return result.changes ?? [];
}

const fmt = (c: ClassifiedChange) =>
  `  • ${c.resourceType} ${c.path}: ${JSON.stringify(c.before ?? null)} → ${JSON.stringify(c.after ?? null)}${c.reason ? `   [${c.reason}]` : ""}`;

/**
 * Build the what-if report + exit code. exitCode 0 ⇔ nothing unexpected.
 *
 * `redact` (default false) routes both the human and machine-readable
 * renderings through the value-free functions in ./whatif-redact.js instead of
 * this file's own `before`/`after`-carrying renderer. The default stays
 * value-carrying on purpose: someone running this in their own terminal is
 * the diagnostic case `before`/`after` exists for. The redaction guarantee
 * belongs here, at this boundary, where it is one flag and a unit test — not
 * in caller shell (e.g. a workflow step) that could silently drift back to
 * printing values.
 */
export function reportWhatIf(r: ClassifiedResult, opts: { json: boolean; redact?: boolean }): { output: string; exitCode: number; diagnostic?: string } {
  const exitCode = r.unexpected.length === 0 ? 0 : 1;
  if (opts.redact) {
    // TWO TIERS, deliberately different (see whatif-redact.ts):
    //   `output`     — counts only. This is what the drift gate copies into a
    //                  PERMANENT PUBLIC issue, so it carries no resource names.
    //   `diagnostic` — type, name, path, reason; no before/after. The tier meant
    //                  for a build log, where a reader with repo access can
    //                  actually act on the number.
    // Emitting only the first is why the gate could publish "11 unexpected",
    // tell the reader to open the run, and have the run hold nothing but that
    // same 11. Returned separately rather than merged so the caller cannot
    // accidentally route the diagnostic tier to the public surface.
    const output = opts.json ? JSON.stringify({ summary: redactForReport(r), exitCode }, null, 2) : redactForLog(r);
    return { output, exitCode, ...(opts.json ? { diagnostic: redactForLog(r) } : {}) };
  }
  if (opts.json) {
    return { output: JSON.stringify({ summary: { noise: r.noise.length, pending: r.pending.length, unexpected: r.unexpected.length }, pending: r.pending, unexpected: r.unexpected, exitCode }, null, 2), exitCode };
  }
  const lines: string[] = [];
  const total = r.noise.length + r.pending.length + r.unexpected.length;
  lines.push(`what-if: ${total.toString()} changes — ${r.noise.length.toString()} known-noise (dropped), ${r.pending.length.toString()} known-pending, ${r.unexpected.length.toString()} un-catalogued`);
  if (r.pending.length) {
    lines.push("", "known-pending (expected, will apply):");
    for (const c of r.pending) lines.push(fmt(c));
  }
  if (r.unexpected.length) {
    lines.push("", "⚠ UN-CATALOGUED (review before any apply):");
    for (const c of r.unexpected) lines.push(fmt(c));
    lines.push("", `✗ ${r.unexpected.length.toString()} un-catalogued change(s) — not safe to apply blindly.`);
  } else {
    lines.push("", "✓ only known noise + expected pending — safe to review for an approved apply.");
  }
  return { output: lines.join("\n"), exitCode };
}

/**
 * Exit code for "the comparison did not run to completion" — the underlying
 * `az` call failed, was throttled, was passed a bad parameter, etc.
 * Deliberately distinct from BOTH `reportWhatIf`'s exit codes (0 = ran,
 * clean; 1 = ran, found unexpected changes) so a caller — a workflow, or
 * anything else driving this command — can tell "nothing to report" apart
 * from "we don't know" purely from the exit code, never by guessing from
 * output shape. A caller MUST treat this code as "no comparison was
 * performed" and never as a drift finding.
 */
export const WHATIF_RUN_FAILED_EXIT_CODE = 3;

/**
 * Build the report for a comparison that THREW before it could produce a
 * result — the counterpart to `reportWhatIf`, which reports on a comparison
 * that ran to completion. Always exits `WHATIF_RUN_FAILED_EXIT_CODE`.
 *
 * `redact` governs this failure path exactly the way it governs the success
 * path in `reportWhatIf`: with it set, the rendered message must not
 * reproduce the underlying command line or its parameters — see
 * `redactWhatIfFailure`. The default stays value-carrying, for the same
 * reason `reportWhatIf`'s does: a local, interactive run is the diagnostic
 * case that needs the real message.
 */
export function reportWhatIfFailure(err: unknown, opts: { json: boolean; redact?: boolean }): { output: string; exitCode: number } {
  const raw = err instanceof Error ? err.message : String(err);
  const message = opts.redact ? redactWhatIfFailure(raw) : raw;
  const exitCode = WHATIF_RUN_FAILED_EXIT_CODE;
  if (opts.json) {
    return { output: JSON.stringify({ ranToCompletion: false, error: message, exitCode }, null, 2), exitCode };
  }
  return { output: `comparison did not run to completion — NOT a drift finding: ${message}`, exitCode };
}
